/**
 * Dashboard Page
 * Main authenticated landing page showing overview and navigation
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createAPIClient, ICustomersApi, IJobsApi } from '../api';
import * as types from '../api/types';
import { useAuth } from '../context/AuthContext';
import { canManageCustomers, canManageJobs, canReadCustomers, canReadJobs } from '../utils/permissions';
import { formatJobMoney } from '../utils/jobPresentation';
import { Toast } from '../components/Toast';
import { SetupChecklistCard } from '../components/SetupChecklistCard';

export function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const apiClient = React.useMemo<ICustomersApi & IJobsApi>(() => createAPIClient(), []);

  // State
  const [customers, setCustomers] = useState<types.Customer[]>([]);
  const [jobs, setJobs] = useState<types.Job[]>([]);
  const [customerCount, setCustomerCount] = useState<number | null>(null);
  const [jobCount, setJobCount] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load data
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Fetch summary data. The backend denies CUSTOMER_PORTAL the
        // customer list (no per-customer scoping yet), so it is not requested.
        const [customersResult, jobsResult] = await Promise.allSettled([
          canReadCustomers(user) ? apiClient.listCustomers(1, 5) : Promise.resolve(null),
          canReadJobs(user) ? apiClient.listJobs(1, 5) : Promise.resolve(null),
        ]);

        if (customersResult.status === 'fulfilled' && customersResult.value) {
          setCustomers(customersResult.value.data);
          setCustomerCount(customersResult.value.pagination.total);
        } else if (canReadCustomers(user)) {
          setError('Recent customers could not be loaded.');
        }
        if (jobsResult.status === 'fulfilled' && jobsResult.value) {
          setJobs(jobsResult.value.data);
          setJobCount(jobsResult.value.pagination.total);
        } else if (canReadJobs(user)) {
          setError((current) => current ? `${current} Recent jobs could not be loaded.` : 'Recent jobs could not be loaded.');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load dashboard';
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    loadDashboardData();
  }, [apiClient, user]);

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Dashboard</h1>

      {error && <Toast kind="error">{error}</Toast>}

      {/* Guided setup checklist (ADMIN only; renders nothing for other roles) */}
      <div className="mb-8 empty:hidden"><SetupChecklistCard /></div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      )}

      {/* Dashboard Content */}
      {!isLoading && (
        <div className="space-y-8">
          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {canReadCustomers(user) && <StatCard label="Total Customers" value={customerCount ?? 'Unavailable'} />}
            {canReadJobs(user) && <StatCard label="Total Jobs" value={jobCount ?? 'Unavailable'} />}
          </div>

          {/* Customers Section */}
          {canReadCustomers(user) && <section className="bg-white rounded-lg shadow p-4 sm:p-6">
            <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
              <h2 className="text-xl font-bold text-gray-900">Recent Customers</h2>
              <button
                onClick={() => navigate('/customers')}
                className="text-blue-600 hover:text-blue-700 text-sm font-semibold min-h-[44px] sm:min-h-0 px-2 -mx-2 sm:px-0 sm:mx-0"
              >
                View All →
              </button>
            </div>

            {customerCount === null ? (
              <p className="text-gray-600 text-center py-8">Recent customers unavailable</p>
            ) : customers.length === 0 ? (
              <p className="text-gray-600 text-center py-8">No customers yet</p>
            ) : (
              <div className="space-y-2">
                {customers.map((customer) => (
                  <div
                    key={customer.id}
                    className="flex justify-between items-center gap-3 p-3 bg-gray-50 rounded hover:bg-gray-100 transition"
                  >
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 break-words">{customer.name}</p>
                      {customer.email && <p className="text-sm text-gray-600 break-all">{customer.email}</p>}
                    </div>
                    <button
                      onClick={() => navigate(`/customers/${customer.id}`)}
                      className="text-blue-600 hover:text-blue-700 text-sm shrink-0 min-h-[44px] sm:min-h-0 px-2 sm:px-0"
                    >
                      View
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>}

          {/* Jobs Section */}
          {canReadJobs(user) && <section className="bg-white rounded-lg shadow p-4 sm:p-6">
            <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
              <h2 className="text-xl font-bold text-gray-900">Recent Jobs</h2>
              <button
                onClick={() => navigate('/jobs')}
                className="text-blue-600 hover:text-blue-700 text-sm font-semibold min-h-[44px] sm:min-h-0 px-2 -mx-2 sm:px-0 sm:mx-0"
              >
                View All →
              </button>
            </div>

            {jobCount === null ? (
              <p className="text-gray-600 text-center py-8">Recent jobs unavailable</p>
            ) : jobs.length === 0 ? (
              <p className="text-gray-600 text-center py-8">No jobs yet</p>
            ) : (
              <div className="space-y-2">
                {jobs.map((job) => (
                  <div
                    key={job.id}
                    className="flex justify-between items-center gap-3 p-3 bg-gray-50 rounded hover:bg-gray-100 transition"
                  >
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900">Job #{job.id.substring(0, 8)}</p>
                      <p className="text-sm text-gray-600">
                        Status: <span className="font-medium break-words">{job.status}</span>
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-semibold text-gray-900">{formatJobMoney(job.total)}</p>
                      <button
                        onClick={() => navigate(`/jobs/${job.id}`)}
                        className="text-blue-600 hover:text-blue-700 text-sm min-h-[44px] sm:min-h-0 px-2 sm:px-0"
                      >
                        View
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>}

          {/* Quick Actions */}
          <section className="bg-white rounded-lg shadow p-4 sm:p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {canManageCustomers(user) && <ActionButton label="New Customer" onClick={() => navigate('/customers/new')} />}
              {canManageJobs(user) && <ActionButton label="New Job" onClick={() => navigate('/jobs/new')} />}
              {canManageJobs(user) && <ActionButton label="New Invoice" onClick={() => navigate('/invoices/new')} />}
            </div>
          </section>
        </div>
      )}
    </main>
  );
}

// ============ Components ============

interface StatCardProps {
  label: string;
  value: string | number;
}

function StatCard({ label, value }: StatCardProps) {
  return (
    <div className="bg-white rounded-lg shadow p-4 sm:p-6">
      <p className="text-gray-600 text-sm font-medium mb-2">{label}</p>
      <p className="text-2xl sm:text-3xl font-bold text-gray-900 break-words">{value}</p>
    </div>
  );
}

interface ActionButtonProps {
  label: string;
  onClick: () => void;
}

function ActionButton({ label, onClick }: ActionButtonProps) {
  return (
    <button
      onClick={onClick}
      className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 sm:py-2 px-4 min-h-[44px] sm:min-h-0 rounded-md transition duration-200"
    >
      {label}
    </button>
  );
}
